import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import LinkedInfoBlock from '../../components/LinkedInfoBlock';
import Level2AuthModal from '../../components/Level2AuthModal';
import LotteryModal from '../../components/LotteryModal';
import LotterySection from '../../components/LotterySection';


// ===== Types =====
type AddressObj = {
  formatted?: string;
  city?: string;
  region?: string;
  country?: string;
  street?: string;
  state?: string;
  postalCode?: string;
};

type ScanLocation = {
  latitude?: number | null;
  longitude?: number | null;
  accuracy?: number | null;
  address?: AddressObj;
  city?: string;
  country?: string;
};

type JepTagsApiResponse = {
  success: boolean;
  message?: string;
  verified?: boolean;
  data?: {
    verified: boolean;
    level: 1 | 2;
    tagId: string;
    scanId: string;
    timestamp: string;
    product?: any;
    business?: any;
    traceability?: any;
    authenticity?: any;
    stats?: any;
    campaign?: any;
    consumer?: any;
    upgrade?: any;
    lotteryInfo?: any;
    isActivated?: boolean;
    activationStatus?: 'activated' | 'pending' | 'not_activated';
  };
  error?: string;
};

type Business = { companyName?: string; email?: string; industry?: string; [k: string]: any };
type Product = { name?: string; batchId?: string; sku?: string; category?: string; [k: string]: any };
type Store = { storeName?: string; manager?: string; email?: string; latitude?: number; longitude?: number; address?: AddressObj; [k: string]: any };

type Props = {
  baseUrl?: string;
  useLevel2?: boolean;
  embedded?: boolean;
};

type Lottery = {
  lotteryId: string;
  name: string;
  description?: string;
  status: 'active' | 'ended' | 'pending';
  drawDate: string;
  registrationOpens: string;
  registrationCloses?: string;
  totalPrizes: number;
  canJoin: boolean;
  alreadyEntered: boolean;
  requiresGPS: boolean;
  requiresActivation: boolean;
  maxEntriesPerTag: number;
};

type LotteryInfoResponse = {
  success: boolean;
  lotteries?: Lottery[];
  message?: string;
};

type LotteryJoinResponse = {
  success: boolean;
  message?: string;
  hasWon?: boolean;
  entry?: { _id: string; entryNumber: string; timestamp: string; };
  prize?: { name: string; description?: string; value?: string; };
  lottery?: { name: string; drawDate: string; };
};

// ============================================================
// ✅ resolveTagId — handles ALL scan input formats:
//    1. Plain TagID:       "VIETNAM03"
//    2. NFC UID path:      "http://jeptags.com/uid/UID:04A1B2C3"
//    3. NFC query string:  "http://jeptags.com/scan?UID=04A1B2C3"
//    4. GS1 Digital Link:  "https://jeptags.com/01/0614141/21/TAG123"
//    5. Direct scan URL:   "https://jeptags.com/scan/TAG12345678"
//    6. Raw UID prefix:    "UID:04A1B2C3D4E5F6"
// ============================================================
function resolveTagId(input: string): string {
  if (!input) return input;
  const raw = input.trim();

  if (!raw.includes('://') && !raw.startsWith('www.')) {
    return raw.replace(/^UID:/i, '');
  }

  try {
    const url = new URL(raw.startsWith('http') ? raw : 'https://' + raw);
    const path = url.pathname;

    const qTagId = url.searchParams.get('tagId') ||
                   url.searchParams.get('UID')   ||
                   url.searchParams.get('uid');
    if (qTagId) return qTagId.replace(/^UID:/i, '');

    const uidMatch = path.match(/\/uid\/(?:UID:)?([^\/\?]+)/i);
    if (uidMatch) return uidMatch[1];

    const gs1Match = path.match(/\/01\/\d+\/21\/([^\/\?]+)/);
    if (gs1Match) return gs1Match[1];

    const scanMatch = path.match(/\/(?:scan|verify)\/([^\/\?]+)/);
    if (scanMatch) return scanMatch[1];

    const parts = path.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    return last ? last.replace(/^UID:/i, '') : raw;
  } catch {
    return raw;
  }
}

// ============================================================
// ✅ normalizeApiResponse — handles BOTH backend response formats:
//    Format A: { success: true, data: { verified: true, ... } }  ← standard
//    Format B: { success: true, tag: {...}, lotteryInfo: {...} }  ← your API
// ============================================================
function normalizeApiResponse(
  result: any,
  tagId: string,
  level: 1 | 2
): JepTagsApiResponse {
  if (!result.success) return result;

  // Format A — already has data.verified
  if (result.data?.verified !== undefined) return result;

  // Format B — has tag object at top level
  if (result.tag) {
    return {
      ...result,
      data: {
        verified: true,
        level,
        tagId,
        scanId: result.scanId || result.tag?.scanId || '',
        timestamp: new Date().toISOString(),
        // Spread tag fields (product, business, traceability etc)
        product:       result.tag.product       || result.product,
        business:      result.tag.business      || result.business,
        traceability:  result.tag.traceability  || result.traceability,
        authenticity:  result.tag.authenticity  || result.authenticity,
        stats:         result.tag.stats         || result.stats,
        campaign:      result.tag.campaign      || result.campaign,
        consumer:      result.tag.consumer      || result.consumer,
        upgrade:       result.tag.upgrade       || result.upgrade,
        isActivated:   result.tag.activation?.isActivated,
        activationStatus: result.tag.activation?.status,
        lotteryInfo:   result.lotteryInfo,
        // Keep raw tag for debugging
        _rawTag: result.tag,
      }
    };
  }

  return result;
}

const ScanConsumerPage: React.FC<Props> = ({
  baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? `${window.location.protocol}//${window.location.hostname}:3000/api/scanConsumer`
    : `${window.location.origin}/api/scanConsumer`,
  useLevel2 = false,
  embedded = false,
}) => {

  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // ✅ Resolve tagId from ALL possible URL formats
  const rawUrlTagId =
    searchParams.get('tagId') ||
    searchParams.get('UID')   ||
    searchParams.get('uid')   ||
    params.tagId              ||
    params.uid                ||
    undefined;

  const urlTagId = rawUrlTagId ? resolveTagId(rawUrlTagId) : undefined;

  // State
  const [qrTagInput, setQrTagInput] = useState(urlTagId || "");
  const [qrStatus, setQrStatus] = useState<null | { kind: "info" | "error" | "ok"; text: string }>(null);
  const [qrResult, setQrResult] = useState<JepTagsApiResponse | null>(null);

  const [nfcTagInput, setNfcTagInput] = useState("");
  const [nfcStatus, setNfcStatus] = useState<string>("checking");
  const [nfcResult, setNfcResult] = useState<JepTagsApiResponse | null>(null);

  const [currentScanLocation, setCurrentScanLocation] = useState<ScanLocation | undefined>(undefined);
  const [store, setStore] = useState<Store | undefined>(undefined);

  const [lotteries, setLotteries] = useState<Lottery[] | null>(null);
  const [showLotteryModal, setShowLotteryModal] = useState(false);
  const [selectedLottery, setSelectedLottery] = useState<Lottery | null>(null);
  const [lotteryLoading, setLotteryLoading] = useState(false);

  const [isL2Authed, setIsL2Authed] = useState<boolean>(() => {
    try { return localStorage.getItem('authLevel') === '2'; }
    catch { return false; }
  });

  const getCurrentLevel = useCallback((): 1 | 2 => (isL2Authed ? 2 : 1), [isL2Authed]);
  const [currentLevel, setCurrentLevel] = useState(() => getCurrentLevel());
  const [requestCount, setRequestCount] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const mountedRef = useRef<boolean>(true);
  const latestRequestRef = useRef<number>(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    const newLevel = getCurrentLevel();
    if (newLevel !== currentLevel) setCurrentLevel(newLevel);
  }, [isL2Authed, getCurrentLevel, currentLevel]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authLevel') {
        const newIsL2Authed = localStorage.getItem('authLevel') === '2';
        if (newIsL2Authed !== isL2Authed) setIsL2Authed(newIsL2Authed);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isL2Authed]);

  const setQrInfo = (text: string) => setQrStatus({ kind: "info", text });
  const setQrOk  = (text: string) => setQrStatus({ kind: "ok",   text });
  const setQrErr = (text: string) => setQrStatus({ kind: "error", text });

  const authHeaders = useCallback(() => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    let token = localStorage.getItem("consumerToken");
    if (!token) {
      const match = document.cookie.match(/token=([^;]+)/);
      if (match) token = match[1];
    }
    if (token) headers["Authorization"] = `Bearer ${token}`;
    else console.warn("⚠️ No consumer token found");
    return headers;
  }, []);

  const getHeaderInfo = () => {
    if (getCurrentLevel() === 2) {
      return {
        title: "Level 2 (Full Traceability)",
        description: "Enhanced scanning with complete product traceability and rewards",
        badge: "🔒 Level 2 - Full Access",
        badgeColor: "bg-yellow-500"
      };
    }
    return {
      title: "Level 1 (Anonymous Scanning)",
      description: "Verify product authenticity without registration",
      badge: "🌐 Level 1 - Public Access",
      badgeColor: "bg-green-500"
    };
  };

  const getScanLocation = useCallback(async (): Promise<ScanLocation | undefined> => {
    if (!("geolocation" in navigator)) return undefined;
    return new Promise<ScanLocation | undefined>((resolve) => {
      // ✅ 8 second timeout — don't block scan forever
      const geoTimeout = setTimeout(() => {
        console.log('⏱️ Location timeout — scanning without GPS');
        resolve(undefined);
      }, 8000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(geoTimeout);
          const loc: ScanLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy || 10
          };
          console.log('✅ Got location:', loc);
          setCurrentScanLocation(loc);
          resolve(loc);
        },
        (error) => {
          clearTimeout(geoTimeout);
          console.warn("Location error:", error);
          resolve(undefined);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      );
    });
  }, []);

  // ============================================================
  // ✅ onManualVerify — handles both API response formats
  // ============================================================
  const onManualVerify = useCallback(async () => {
    const rawInput = qrTagInput.trim() || nfcTagInput.trim();
    if (!rawInput) {
      setQrErr("❌ Please enter a valid Tag ID");
      return;
    }

    const tagId = resolveTagId(rawInput);
    console.log(`🔍 Input: "${rawInput}" → Resolved TagID: "${tagId}"`);

    const thisRequestId = ++latestRequestRef.current;
    setQrInfo("🔄 Verifying Tag...");
    setQrResult(null);
    setStore(undefined);
    setLotteries(null);
    setRequestCount(c => c + 1);

    const actualLevel = getCurrentLevel();
    const apiUrl = `${baseUrl}/level${actualLevel}/${encodeURIComponent(tagId)}`;
    const location = await getScanLocation();

    console.log(`📡 Scanning at Level ${actualLevel}:`, apiUrl);

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify({
          location: location || undefined,
          deviceInfo: {
            userAgent: navigator.userAgent || "unknown",
            platform: navigator.platform || "unknown",
            scanType: urlTagId ? 'url' : 'manual'
          }
        })
      });

      if (thisRequestId !== latestRequestRef.current) return;

      const raw = await response.json();
      console.log("📥 Response:", raw);

      if (!raw.success) {
        setQrErr(`❌ ${raw.message || 'Tag verification failed'}`);
        setQrResult(raw);
        return;
      }

      // ✅ Normalize both response formats
      const result = normalizeApiResponse(raw, tagId, actualLevel);

      if (result.data?.verified) {
        setQrOk(`✅ Tag verified! Level ${actualLevel} scan complete.`);
        setQrResult(result);
        setCurrentScanLocation(location);
        // Debug helper
        (window as any).__scanResult = result;
        console.log('✅ Scan result saved. data.verified:', result.data?.verified);
      } else {
        setQrErr(`❌ ${raw.message || 'Tag not verified'}`);
        setQrResult(result);
      }

    } catch (error: any) {
      if (thisRequestId !== latestRequestRef.current) return;
      console.error("Verification error:", error);
      setQrErr(`❌ Network error: ${error?.message || 'Failed to verify tag'}`);
    }
  }, [qrTagInput, nfcTagInput, baseUrl, authHeaders, getCurrentLevel, getScanLocation, urlTagId]);

  // ============================================================
  // ✅ Auto-verify — StrictMode safe using local cancelled flag
  //    Works in both development (StrictMode) and production
  // ============================================================
  useEffect(() => {
    if (!urlTagId || !urlTagId.trim()) return;

    console.log("🔗 Tag ID detected in URL:", urlTagId);
    console.log("🚀 Auto-verifying tag from NFC/QR scan...");

    setQrTagInput(urlTagId);
    const tagId = resolveTagId(urlTagId);

    // ✅ Local cancelled flag — StrictMode safe
    // StrictMode: run1 cancelled=false → cleanup sets cancelled=true
    //             run2 cancelled=false (new variable!) → completes ✅
    let cancelled = false;

    const doVerify = async () => {
      if (cancelled || !mountedRef.current) return;

      const thisRequestId = ++latestRequestRef.current;
      setQrInfo("🔄 Verifying Tag...");
      setQrResult(null);
      setLotteries(null);
      setRequestCount(c => c + 1);

      const actualLevel = getCurrentLevel();
      const apiUrl = `${baseUrl}/level${actualLevel}/${encodeURIComponent(tagId)}`;

      console.log(`📍 Getting location for Level ${actualLevel} scan...`);
      const location = await getScanLocation();

      if (cancelled || thisRequestId !== latestRequestRef.current) return;

      console.log(`📡 Auto-scanning Level ${actualLevel}:`, apiUrl);

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: authHeaders(),
          credentials: "include",
          body: JSON.stringify({
            location: location || undefined,
            deviceInfo: {
              userAgent: navigator.userAgent || "unknown",
              platform: navigator.platform || "unknown",
              scanType: 'url'
            }
          })
        });

        if (cancelled || thisRequestId !== latestRequestRef.current) return;

        const raw = await response.json();
        console.log("📥 Auto-verify response:", raw);

        if (!raw.success) {
          setQrErr(`❌ ${raw.message || 'Tag verification failed'}`);
          setQrResult(raw);
          return;
        }

        // ✅ Normalize both response formats
        const result = normalizeApiResponse(raw, tagId, actualLevel);

        if (result.data?.verified) {
          setQrOk(`✅ Tag verified! Level ${actualLevel} scan complete.`);
          setQrResult(result);
          setCurrentScanLocation(location);
          // Debug helper
          (window as any).__scanResult = result;
          console.log('✅ Auto-verify saved. data.verified:', result.data?.verified);
          console.log('✅ data keys:', Object.keys(result.data || {}));
        } else {
          setQrErr(`❌ ${raw.message || 'Tag not verified'}`);
          setQrResult(result);
        }

      } catch (error: any) {
        if (cancelled || thisRequestId !== latestRequestRef.current) return;
        console.error("Auto-verify error:", error);
        setQrErr(`❌ Network error: ${error?.message || 'Failed to verify tag'}`);
      }
    };

    const timer = setTimeout(doVerify, 500);

    // ✅ Cleanup cancels this run — new run gets fresh cancelled=false
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTagId]);

  const formatDateSafe = (date: any): string => {
    if (!date) return 'Date not set';
    try {
      return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return String(date); }
  };

  const handleAuthSuccess = useCallback((newLevel: number, token?: string) => {
    setIsL2Authed(newLevel === 2);
    setCurrentLevel(newLevel as 1 | 2);
    localStorage.setItem('authLevel', String(newLevel));
    if (token) localStorage.setItem('consumerToken', token);
    setShowAuthModal(false);
    onManualVerify();
  }, [onManualVerify]);

  const handleAuthError = useCallback((error: string) => {
    setQrErr(`❌ Authentication failed: ${error}`);
  }, []);

  const toggleScanLevel = () => {
    if (getCurrentLevel() === 1) setShowAuthModal(true);
  };

  const handleLogout = () => {
    setIsL2Authed(false);
    setCurrentLevel(1);
    localStorage.removeItem('authLevel');
    localStorage.removeItem('consumerToken');
    setQrOk("Switched to Level 1");
  };

  // ============================================================
  // ✅ joinLottery — resolves tagId before API call
  // ============================================================
  const joinLottery = useCallback(async (lottery: Lottery) => {
    console.log('🎰 Lottery button clicked!', lottery.lotteryId);

    const rawInput = qrTagInput.trim() || nfcTagInput.trim();
    if (!rawInput) { alert('No tag ID available'); return; }

    const tagId = resolveTagId(rawInput);
    console.log(`✅ TagID for lottery: "${tagId}"`);

    setLotteryLoading(true);

    try {
      if (lottery.requiresGPS) {
        if (!("geolocation" in navigator)) throw new Error('Geolocation not supported');
        if (navigator.permissions) {
          try {
            const ps = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
            if (ps.state === 'denied') throw new Error('Location permission denied');
          } catch (permErr) { console.warn('Permission check not supported:', permErr); }
        }
      }

      const location = await getScanLocation();

      if (lottery.requiresGPS && (!location?.latitude || !location?.longitude)) {
        throw new Error('GPS location required but unavailable. Please enable location access.');
      }

      const body: any = {
        deviceInfo: { userAgent: navigator.userAgent, platform: navigator.platform },
      };
      if (location?.latitude != null && location?.longitude != null) {
        body.userLatitude = location.latitude;
        body.userLongitude = location.longitude;
      }

      const API_BASE = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? "" : window.location.origin;

      const url = `${API_BASE}/api/consumer-lottery/join/${encodeURIComponent(tagId)}?lotteryId=${lottery.lotteryId}`;
      console.log('🌐 Lottery API:', url);

      const resp = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify(body),
      });

      let result: LotteryJoinResponse;
      try { result = await resp.json(); }
      catch { result = { success: false, message: 'Invalid response from server' }; }

      console.log('📥 Lottery response:', result);

      if (result.success) {
        if (mountedRef.current) { setLotteryLoading(false); setShowLotteryModal(false); }
        await new Promise(r => setTimeout(r, 100));
        navigate(`/consumer/lottery-spin/${tagId}/${lottery.lotteryId}`, {
          state: { result, lottery, entryId: result.entry?._id },
        });
        return;
      } else {
        alert(result.message || 'Failed to join lottery');
      }
    } catch (err: any) {
      console.error('❌ Lottery error:', err);
      alert(`Failed to join lottery: ${err?.message || err}`);
    } finally {
      if (mountedRef.current) { setLotteryLoading(false); setShowLotteryModal(false); }
    }
  }, [qrTagInput, nfcTagInput, navigate, authHeaders, getScanLocation]);

  // ============================================================
  //  UI Components
  // ============================================================
  const AuthControls = () => {
    const actualLevel = getCurrentLevel();
    const headerInfo = getHeaderInfo();
    return (
      <div className="mt-4 flex items-center justify-center gap-4">
        <span className={`px-4 py-2 rounded-full text-sm font-semibold text-white ${headerInfo.badgeColor}`}>
          {headerInfo.badge}
        </span>
        {actualLevel === 1 ? (
          <button type="button" onClick={toggleScanLevel}
            className="px-4 py-2 bg-white text-gray-800 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            Enable Level 2
          </button>
        ) : (
          <div className="flex gap-2">
            <span className="px-3 py-1 bg-white bg-opacity-20 rounded-lg text-sm text-white">Level 2 Active</span>
            <button type="button" onClick={handleLogout}
              className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors">
              Switch to Level 1
            </button>
          </div>
        )}
      </div>
    );
  };

  const scanResult = qrResult || nfcResult;

  const LotterySection = () => {
    if (!scanResult?.data?.verified) return null;
    const lotteryInfo = (scanResult.data as any)?.lotteryInfo;
    if (!lotteryInfo) return null;
    if (!lotteryInfo.showLotteryButton) {
      return lotteryInfo.message ? (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">ℹ️ {lotteryInfo.message}</p>
        </div>
      ) : null;
    }
    const campaigns = lotteryInfo.campaigns || [];
    if (campaigns.length === 0) {
      return (
        <div className="mt-4 bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600">No lottery campaigns available at this time.</p>
        </div>
      );
    }
    return (
      <div className="mt-4 space-y-3">
        <h3 className="font-bold text-gray-800 flex items-center">
          <span className="text-2xl mr-2">🎰</span>
          Available Lotteries ({campaigns.length})
        </h3>
        {campaigns.map((campaign: any) => (
          <div key={campaign.id} className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-lg">{campaign.name}</h4>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Active</span>
            </div>
            {campaign.description && <p className="text-sm text-gray-600 mb-3">{campaign.description}</p>}
            <div className="text-xs text-gray-500 mb-3 space-y-1">
              <div>🎁 Total Prizes: {campaign.prizes?.length || 0}</div>
              <div>📅 Draw: {formatDateSafe(campaign.drawDate)}</div>
              {campaign.rules?.requireGPS && <div>📍 GPS Required</div>}
              {campaign.rules?.maxEntriesPerTag && <div>🎫 Max Entries: {campaign.rules.maxEntriesPerTag}</div>}
            </div>
            <button type="button"
              onClick={() => joinLottery({
                lotteryId: campaign.lotteryId || campaign.id,
                name: campaign.name,
                description: campaign.description,
                drawDate: campaign.drawDate,
                prizes: campaign.prizes,
                rules: campaign.rules,
                stats: campaign.stats
              } as any)}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-colors">
              🎰 Join Lottery Now!
            </button>
          </div>
        ))}
      </div>
    );
  };

  const handleKeyDownVerify = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); onManualVerify(); }
  };

  const content = (
    <>
      {!embedded && (
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold text-white drop-shadow mb-2 flex items-center justify-center gap-2">
            <span role="img" aria-label="tag">🏷️</span>
            JepTags Consumer Scanner - {getHeaderInfo().title}
          </h1>
          <p className="text-lg text-gray-200 font-light">{getHeaderInfo().description}</p>
          {urlTagId && (
            <div className="mt-3 inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white">
              🔗 Scanned via NFC/QR Code
            </div>
          )}
          <AuthControls />
        </header>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 w-full max-w-4xl">
        {/* Manual Tag Entry */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
          <h2 className="text-2xl font-bold text-center">Manual Tag Entry</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="qrInput" className="block font-semibold text-gray-700 mb-2">QR Code / Tag ID:</label>
              <input id="qrInput" value={qrTagInput} onChange={e => setQrTagInput(e.target.value)}
                placeholder="Enter Tag ID (e.g., NGOTTRAN123V18)" className="w-full border rounded-lg px-3 py-2"
                onKeyDown={handleKeyDownVerify} />
            </div>
            <div>
              <label htmlFor="nfcInput" className="block font-semibold text-gray-700 mb-2">NFC Tag ID:</label>
              <input id="nfcInput" value={nfcTagInput} onChange={e => setNfcTagInput(e.target.value)}
                placeholder="Enter NFC Tag ID" className="w-full border rounded-lg px-3 py-2"
                disabled={nfcStatus === "unsupported"} onKeyDown={handleKeyDownVerify} />
              <p className="text-xs text-gray-500 mt-1">
                NFC Status: {nfcStatus === "checking" ? "Checking..." : nfcStatus === "unsupported" ? "Not supported" : nfcStatus}
              </p>
            </div>
            <button type="button" onClick={onManualVerify}
              disabled={(!qrTagInput.trim() && !nfcTagInput.trim()) || qrStatus?.kind === "info"}
              className="w-full px-5 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-blue-400 text-white font-semibold hover:from-purple-600 hover:to-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {qrStatus?.kind === "info" ? "🔄 Verifying..." : "🔍 Verify Tag"}
            </button>
          </div>

          {qrStatus && (
            <div role="status" aria-live="polite"
              className={`rounded-lg px-4 py-3 text-sm border text-center ${
                qrStatus.kind === "ok"    ? "bg-green-50 text-green-800 border-green-200" :
                qrStatus.kind === "error" ? "bg-red-50 text-red-800 border-red-200" :
                                            "bg-blue-50 text-blue-800 border-blue-200"
              }`}>
              {qrStatus.text}
            </div>
          )}

          <LotterySection />
        </div>

        {/* Scan Results */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
          <h2 className="text-2xl font-bold text-center">Scan Results</h2>
          {scanResult && scanResult.data ? (
            <LinkedInfoBlock
              scanData={scanResult.data}
              currentLocation={currentScanLocation}
              onUpgrade={() => { if (getCurrentLevel() === 1) setShowAuthModal(true); }}
              onShare={() => {
                const shareText = `Product verification: ${scanResult.data?.verified ? 'AUTHENTIC' : 'NOT AUTHENTIC'} - Tag: ${scanResult.data?.tagId}`;
                if (navigator.share) {
                  navigator.share({ title: 'JepTags Verification Result', text: shareText, url: window.location.href })
                    .catch(() => navigator.clipboard.writeText(shareText).catch(() => {}));
                } else {
                  navigator.clipboard.writeText(shareText).catch(() => {});
                }
              }}
            />
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="text-6xl mb-4">🏷️</div>
              <p>Enter a Tag ID above to verify and see detailed results</p>
              <p className="text-sm text-gray-400 mt-2">Try: NGOTTRAN123V18 (authentic) or any other tag</p>
              {urlTagId && <p className="text-sm text-blue-600 mt-3">Auto-verifying {urlTagId}...</p>}
            </div>
          )}
        </div>
      </div>

      {!embedded && (
        <footer className="text-xs text-gray-200 mt-10 text-center max-w-4xl">
          <div className="mb-2">Requests: <b>{requestCount}</b> | API: scanConsumer | Level {getCurrentLevel()} Mode</div>
          {currentScanLocation && (
            <div>
              Location: {currentScanLocation.latitude?.toFixed(4)}, {currentScanLocation.longitude?.toFixed(4)}
            </div>
          )}
          <div className="mt-2 text-gray-300">Consumer verification app — For business management, visit /business</div>
        </footer>
      )}

      <Level2AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
        onAuthError={handleAuthError}
      />

      <LotteryModal
        isOpen={showLotteryModal}
        lotteries={lotteries ?? undefined}
        lottery={selectedLottery ?? undefined}
        onClose={() => { setShowLotteryModal(false); setSelectedLottery(null); }}
        onJoin={(lot) => joinLottery(lot)}
        isLoading={lotteryLoading}
      />
    </>
  );

  return embedded
    ? <div className="space-y-6">{content}</div>
    : <div className="min-h-screen bg-gradient-to-br from-blue-400 to-purple-500 p-7 flex flex-col items-center">{content}</div>;
};

export default ScanConsumerPage;